import React, { useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CreateVillageModal = ({ onSave, onClose }) => {
  const [name, setName] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [villageCode, setVillageCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Preview the auto-generated ID
  const previewId = villageCode.trim().toLowerCase() 
    ? `${villageCode.trim().toLowerCase()}_unified` 
    : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError('Village name is required');
      return;
    }
    if (!tenantId.trim()) {
      setError('Tenant ID is required');
      return;
    }
    if (!villageCode.trim()) {
      setError('Village code is required');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/admin/villages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          tenant_id: tenantId.trim(),
          village_code: villageCode.trim().toLowerCase()
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to create village');
      }

      const result = await response.json();
      onSave(result);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4"
        data-testid="create-village-modal"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Create New Village</h2>
            <p className="text-sm text-gray-500">Add a new village management client</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-create-village"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Village Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Kildare Village, Bicester Village"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-village-name"
              autoFocus
            />
          </div>

          {/* Tenant ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tenant ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              placeholder="e.g. bicester_collection, value_retail"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-tenant-id"
            />
            <p className="text-xs text-gray-500 mt-1">
              The organization or tenant this village belongs to
            </p>
          </div>

          {/* Village Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Village Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={villageCode}
              onChange={(e) => setVillageCode(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder="e.g. kildare, bicester, mmv"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="input-village-code"
            />
            <p className="text-xs text-gray-500 mt-1">
              Short unique identifier (lowercase letters, numbers, underscores, hyphens)
            </p>
          </div>

          {/* ID Preview */}
          {previewId && (
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-xs text-gray-500 mb-1">Generated Client ID:</p>
              <p className="text-sm font-mono text-gray-700" data-testid="preview-village-id">
                {previewId}
              </p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded" data-testid="create-village-error">
              {error}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 disabled:opacity-50"
            data-testid="cancel-create-village"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !name.trim() || !tenantId.trim() || !villageCode.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-create-village"
          >
            {saving ? 'Creating...' : 'Create Village'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateVillageModal;
